/**
 * Categories Section
 *
 * Displays top service categories sorted by number of popular subcategories:
 * - Section heading with loading indicator
 * - Top 4 categories with most popular subcategories (data from API)
 * - Category cards grid with subcategories
 * - "View all services" button
 * - No layout shifts during loading
 */

'use client';

import { useRef, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { IconButton } from '@/src/shared/ui/button/IconButton';
import { CategoryCard } from '@/src/entities/category/ui/CategoryCard';
import { useAllCategories } from '@/src/entities/category/model/hooks/useAllCategories';
import { getTopCategoriesByPopularSubcategories, getSubcategoriesById } from '@/src/entities/category/api/fetchAllCategories';

export function CategoriesSection() {
  const t = useTranslations('landing.categories');
  const router = useRouter();
  const locale = useLocale();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Fetch ALL categories once and cache for 10 minutes
  const {
    categoryTree,
    loading,
    error,
    isRefetching,
  } = useAllCategories();

  // Sort categories by number of popular subcategories (all categories, not just 4)
  const topCategories = getTopCategoriesByPopularSubcategories(categoryTree, 20);

  // Check scroll position and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Update arrow visibility when categories load or change
  useEffect(() => {
    checkScrollPosition();
  }, [topCategories]);

  // Update arrow visibility on mount and window resize
  useEffect(() => {
    checkScrollPosition();
    const handleResize = () => checkScrollPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getScrollAmount = () => {
    if (!scrollContainerRef.current) return 927;
    const firstChild = scrollContainerRef.current.firstElementChild as HTMLElement;
    const cardWidth = firstChild?.offsetWidth ?? 285;
    const gap = 24; // gap-6
    return cardWidth * 3 + gap * 3;
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const handleCategoryClick = (categorySlug: string) => {
    router.push(`/${locale}/search?category=${categorySlug}`);
  };

  const handleSubcategoryClick = (subcategorySlug: string) => {
    console.log('Subcategory clicked:', subcategorySlug);
    router.push(`/${locale}/search?subcategory=${subcategorySlug}`);
  };

  const handleViewAll = () => {
    console.log('View all services clicked');
    router.push(`/${locale}/categories`);
  };

  return (
    <section className="container-auto pt-6">
      {/* Section Heading with inline loading/error indicators */}
      <div className="mb-6 md:mb-8 lg:mb-10 flex items-center justify-between gap-4">
        <Typography variant="h1" weight="bold" className="text-text-primary">
          {t('title')}
        </Typography>

        {/* Loading indicator */}
        {loading && (
          <span className="text-sm text-text-secondary animate-pulse">
            {t('loading')}
          </span>
        )}

        {/* Background refetch indicator */}
        {!loading && isRefetching && (
          <span className="text-sm text-text-secondary">
            {t('updating')}
          </span>
        )}

        {/* Error indicator */}
        {error && !loading && (
          <span className="text-sm text-red-600" title={error}>
            {t('errorLoading')}
          </span>
        )}
      </div>

      {/* Categories horizontal scroll with navigation arrows */}
      <div className="relative mb-8 lg:mb-10">
        {/* Left fade gradient */}
        {showLeftArrow && (
          <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
        )}

        {/* Right fade gradient */}
        {showRightArrow && (
          <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
        )}

        {/* Scrollable container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide px-6"
          style={{ scrollPaddingLeft: '24px', scrollPaddingRight: '24px' }}
          onScroll={checkScrollPosition}
        >
          {/* Show skeleton loaders during initial load */}
          {loading && topCategories.length === 0 && (
            <>
              {[1, 2, 3, 4].map((index) => (
                <div
                  key={`skeleton-${index}`}
                  className="flex-none w-[285px] h-[280px] bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </>
          )}

          {/* Show actual categories */}
          {topCategories.map((category) => (
            <div key={category.id} className="flex-none w-[285px]">
              <CategoryCard
                category={category}
                subcategories={getSubcategoriesById(categoryTree, category.id)}
                onClick={handleCategoryClick}
                onSubcategoryClick={handleSubcategoryClick}
              />
            </div>
          ))}

          {/* Empty state message - only if not loading and no data */}
          {!loading && topCategories.length === 0 && (
            <div className="w-full text-center py-8">
              <Typography variant="body" className="text-text-secondary">
                {error ? t('failedToLoad') : t('noCategories')}
              </Typography>
            </div>
          )}
        </div>

        {/* Left Arrow */}
        {showLeftArrow && (
          <div className="absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 z-50">
            <IconButton
              iconName="arrow-left"
              onClick={handleScrollLeft}
              size="lg"
              aria-label="Scroll left"
            />
          </div>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 z-50">
            <IconButton
              iconName="arrow-right"
              onClick={handleScrollRight}
              size="lg"
              aria-label="Scroll right"
            />
          </div>
        )}
      </div>

      {/* View All Button - Always visible */}
      <div className="flex justify-center">
        <button onClick={handleViewAll} className="btn-primary">
          <Typography variant="h3" color="inherit" weight="semibold">
            {t('viewAllServices')}
          </Typography>
        </button>
      </div>
    </section>
  );
}
